import { useState, useEffect } from 'react';
import { Wrench, Clock, Coins, TrendingUp, Store } from 'lucide-react';
import { API_URL } from '../../config/api';

const STATUS_LABELS = {
  received: 'Teslim Alındı',
  sent_to_workshop: 'Atölyeye Gönderildi',
  at_workshop: 'Atölyede',
  in_progress: 'Tamirata Başlandı',
  completed: 'Tamirat Tamamlandı',
  returning_to_store: 'Mağazaya Yolda',
  ready_for_pickup: 'Tamirattan Geldi',
  checked: 'Kontrol Edildi',
  delivered: 'Teslim Edildi'
};
const STATUS_COLORS = {
  delivered: { bg: '#d1fae5', color: '#065f46' },
  ready_for_pickup: { bg: '#d1fae5', color: '#065f46' },
  in_progress: { bg: '#dbeafe', color: '#1e40af' },
  sent_to_workshop: { bg: '#dbeafe', color: '#1e40af' },
  at_workshop: { bg: '#dbeafe', color: '#1e40af' },
  completed: { bg: '#dbeafe', color: '#1e40af' },
  returning_to_store: { bg: '#dbeafe', color: '#1e40af' }
};
const ITEM_TYPE_LABELS = { gold: 'Altın', silver: 'Gümüş' };

const RepairReport = ({ getDateRange, reportStoreFilter, formatCurrency }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      setLoading(true);
      try {
        const dr = getDateRange();
        const params = `start_date=${dr.start}&end_date=${dr.end}&store_filter=${reportStoreFilter}`;
        const res = await fetch(`${API_URL}/reports/repair-stats?${params}`);
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch_();
  }, [getDateRange, reportStoreFilter]);

  if (loading) return <div className="empty-state"><div className="empty-icon">⏳</div><p>Tamir verileri yükleniyor...</p></div>;
  if (!data) return <div className="empty-state"><div className="empty-icon">🔧</div><p>Veri bulunamadı</p></div>;

  const s = data.summary || {};
  const byStore = data.byStore || [];
  const list = data.list || [];
  const avgRevenue = s.total_repairs > 0 ? (s.total_revenue || 0) / s.total_repairs : 0;
  const maxTypeCount = Math.max(...(data.byType || []).map(t => t.count), 1);

  return (
    <div className="tab-content">
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon"><Wrench size={20} /></div>
          <div className="stat-info">
            <div className="stat-value">{s.total_repairs || 0}</div>
            <div className="stat-label">Toplam Tamir</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Coins size={20} style={{ color: '#0f766e' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(s.total_revenue || 0)}</div>
            <div className="stat-label">Toplam Tamir Geliri</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Clock size={20} style={{ color: '#f59e0b' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{s.avg_repair_days ? `${parseFloat(s.avg_repair_days).toFixed(1)} gün` : '-'}</div>
            <div className="stat-label">Ort. Tamir Süresi</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><TrendingUp size={20} style={{ color: '#2ecc71' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(avgRevenue)}</div>
            <div className="stat-label">Ort. Tamir Geliri</div>
          </div>
        </div>
      </div>

      {/* Mağaza Bazlı Dağılım */}
      <div className="data-card">
        <h3>Mağaza Bazlı Tamirat Dağılımı</h3>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Mağaza</th>
                <th>Alınan Tamirat</th>
                <th>Teslim Edilen</th>
                <th>Toplam Gelir</th>
                <th>Ortalama Gelir</th>
              </tr>
            </thead>
            <tbody>
              {byStore.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#999' }}>Bu dönemde tamirat kaydı yok</td></tr>
              ) : byStore.map(st => (
                <tr key={st.store_id}>
                  <td><strong><Store size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />{st.store_name || `Mağaza #${st.store_id}`}</strong></td>
                  <td>{st.intake_count}</td>
                  <td>{st.delivered_count}</td>
                  <td>{formatCurrency(st.revenue_total)}</td>
                  <td>{formatCurrency(st.avg_fee)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tamir Türü Dağılımı */}
      {data.byType && data.byType.length > 0 && (
        <div className="chart-card">
          <h3>Tamir Türü Dağılımı</h3>
          <div className="horizontal-bars">
            {data.byType.map((t, i) => (
              <div key={t.item_type} className="h-bar-item">
                <div className="h-bar-label">
                  <span className="h-bar-name">{ITEM_TYPE_LABELS[t.item_type] || t.item_type}</span>
                  <span className="h-bar-value">{t.count} adet • {formatCurrency(t.revenue)}</span>
                </div>
                <div className="h-bar-track">
                  <div className={`h-bar-fill color-${(i % 5) + 1}`} style={{ width: `${(t.count / maxTypeCount) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tüm Tamiratlar */}
      <div className="data-card">
        <h3>Bu Dönemdeki Tamiratlar ({list.length})</h3>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Müşteri</th>
                <th>Mağaza</th>
                <th>Tür</th>
                <th>Açıklama</th>
                <th>Durum</th>
                <th>Süre</th>
                <th>Ücret</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#999' }}>Bu dönemde tamirat kaydı yok</td></tr>
              ) : list.map(r => {
                const sc = STATUS_COLORS[r.status] || { bg: '#fef3c7', color: '#92400e' };
                return (
                  <tr key={r.id}>
                    <td><strong>{r.customer_name || '-'}</strong></td>
                    <td>{r.store_name || '-'}</td>
                    <td>{ITEM_TYPE_LABELS[r.item_type] || r.item_type || '-'}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.item_description || '-'}</td>
                    <td>
                      <span className="days-badge" style={{ background: sc.bg, color: sc.color }}>
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </td>
                    <td>
                      {r.days_waiting === null || r.days_waiting === undefined ? (
                        <span style={{ color: '#999' }}>-</span>
                      ) : (
                        <span style={{
                          color: r.status !== 'delivered' && r.days_waiting > 14 ? '#e74c3c'
                            : r.status !== 'delivered' && r.days_waiting > 7 ? '#f59e0b' : '#2c3e50',
                          fontWeight: 600
                        }}>
                          {r.days_waiting} gün
                        </span>
                      )}
                    </td>
                    <td>{r.fee ? formatCurrency(r.fee) : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RepairReport;
